import { useState, type FormEvent } from "react";
import { Link } from "react-router";
import { Aviso, Botao, Campo, Chip, Cartao } from "../ui";
import { mensagemDoConsole } from "../../nucleo/console";
import { useAgentesDeAtendimento, useEquipesDeAtendimento } from "../../nucleo/tickets";
import {
  ENCERRAMENTO_EM_BRANCO,
  useAceitarConversa,
  useChamadosDaOrganizacao,
  useEncerrarConversa,
  useTransferirConversa,
  validarEncerramento,
  type ConversaDoAtendimento,
  type FormularioDeEncerramento,
} from "../../nucleo/webchat";
import { Conversa } from "./Conversa";

export interface PropsDoPainel {
  conversa: ConversaDoAtendimento;
  /** O resultado de uma ação que tira a conversa das mãos de quem atende (transferir, encerrar). */
  aoConcluir: (frase: string) => void;
}

const ROTULO_DO_ESTADO = { fila: "Na fila", ativa: "Em atendimento", encerrada: "Encerrada" } as const;
const CHIP_DO_ESTADO = { fila: "aguardando", ativa: "ok", encerrada: "neutro" } as const;

/** A conversa aberta de quem atende: cabeçalho, falas, e as ações de cada estado. */
export function PainelDaConversa({ conversa, aoConcluir }: PropsDoPainel) {
  const aceitar = useAceitarConversa();
  return (
    <Cartao
      titulo={conversa.organizacao_nome}
      data-testid="chat-painel-do-atendimento"
      data-estado={conversa.estado}
      acessorio={<Chip estado={CHIP_DO_ESTADO[conversa.estado]}>{ROTULO_DO_ESTADO[conversa.estado]}</Chip>}
    >
      <p className="nota-rodape mb-3">
        {conversa.solicitante_nome ?? "Pessoa removida"}
        {conversa.equipe_nome ? ` · equipe ${conversa.equipe_nome}` : ""}
        {conversa.fora_do_horario ? " · deixada fora do horário" : ""}
      </p>

      {conversa.estado === "fila" ? (
        <>
          <p className="chat-painel__texto">{conversa.previa}</p>
          {aceitar.isError ? (
            <Aviso tom="erro" className="mb-3">
              {mensagemDoConsole(aceitar.error)}
            </Aviso>
          ) : null}
          <Botao
            disabled={aceitar.isPending}
            data-testid="chat-aceitar"
            onClick={() => aceitar.mutate(conversa.id)}
          >
            Aceitar conversa
          </Botao>
        </>
      ) : (
        <>
          <Conversa
            lado="atendimento"
            conversaId={conversa.id}
            podeEscrever={conversa.estado === "ativa"}
            digitandoAtivo
            rotuloDoCampo="Responder ao cliente"
          />
          {conversa.estado === "ativa" ? (
            <AcoesDaConversa conversa={conversa} aoConcluir={aoConcluir} />
          ) : (
            <Encerrada conversa={conversa} />
          )}
        </>
      )}
    </Cartao>
  );
}

function Encerrada({ conversa }: { conversa: ConversaDoAtendimento }) {
  return (
    <div className="mt-3" data-testid="chat-encerrada-do-atendimento">
      {conversa.ticket_id ? (
        <p>
          Registrada no chamado{" "}
          <Link to={`/admin/chamados/${conversa.ticket_id}`}>#{conversa.ticket_numero}</Link>.
        </p>
      ) : null}
      {conversa.avaliacao_nota !== null ? (
        <p data-testid="chat-avaliacao-recebida">
          Avaliação do cliente: <b>{conversa.avaliacao_nota} de 5</b>
          {conversa.avaliacao_comentario ? ` — “${conversa.avaliacao_comentario}”` : ""}
        </p>
      ) : (
        <p className="nota-rodape">O cliente ainda não avaliou este atendimento.</p>
      )}
    </div>
  );
}

function AcoesDaConversa({ conversa, aoConcluir }: PropsDoPainel) {
  const [acao, setAcao] = useState<"transferir" | "encerrar" | null>(null);
  return (
    <div className="mt-3">
      <div className="acoes-do-console">
        <Botao
          variante="fantasma"
          tamanho="pequeno"
          aria-pressed={acao === "transferir"}
          data-testid="chat-abrir-transferencia"
          onClick={() => setAcao(acao === "transferir" ? null : "transferir")}
        >
          Transferir
        </Botao>
        <Botao
          variante="verde"
          tamanho="pequeno"
          aria-pressed={acao === "encerrar"}
          data-testid="chat-abrir-encerramento"
          onClick={() => setAcao(acao === "encerrar" ? null : "encerrar")}
        >
          Encerrar
        </Botao>
      </div>
      {acao === "transferir" ? <Transferencia conversa={conversa} aoConcluir={aoConcluir} /> : null}
      {acao === "encerrar" ? <Encerramento conversa={conversa} aoConcluir={aoConcluir} /> : null}
    </div>
  );
}

function Transferencia({ conversa, aoConcluir }: PropsDoPainel) {
  const agentes = useAgentesDeAtendimento();
  const equipes = useEquipesDeAtendimento();
  const transferir = useTransferirConversa(conversa.id);
  const [tipo, setTipo] = useState<"pessoa" | "equipe">("pessoa");
  const [destino, setDestino] = useState("");

  function enviar(evento: FormEvent) {
    evento.preventDefault();
    if (!destino) return;
    transferir.mutate(tipo === "pessoa" ? { atendenteId: destino } : { equipeId: destino }, {
      onSuccess: () =>
        aoConcluir(
          tipo === "pessoa" ? "Conversa transferida para a pessoa escolhida." : "Conversa devolvida para a fila da equipe.",
        ),
    });
  }

  const opcoes =
    tipo === "pessoa"
      ? (agentes.data ?? []).filter((agente) => agente.id !== conversa.atendente_id)
      : (equipes.data ?? []);

  return (
    <form className="mt-3" onSubmit={enviar} data-testid="chat-transferencia">
      <div className="campo">
        <label className="campo__rotulo" htmlFor="chat-transferir-tipo">
          Transferir para
        </label>
        <select
          id="chat-transferir-tipo"
          className="campo__controle"
          value={tipo}
          onChange={(evento) => {
            setTipo(evento.target.value as "pessoa" | "equipe");
            setDestino("");
          }}
        >
          <option value="pessoa">Outra pessoa</option>
          <option value="equipe">Uma equipe (volta para a fila)</option>
        </select>
      </div>
      <div className="campo">
        <label className="campo__rotulo" htmlFor="chat-transferir-destino">
          {tipo === "pessoa" ? "Pessoa" : "Equipe"}
        </label>
        <select
          id="chat-transferir-destino"
          className="campo__controle"
          value={destino}
          onChange={(evento) => setDestino(evento.target.value)}
        >
          <option value="">Escolha…</option>
          {opcoes.map((opcao) => (
            <option key={opcao.id} value={opcao.id}>
              {opcao.nome}
            </option>
          ))}
        </select>
      </div>
      {transferir.isError ? (
        <Aviso tom="erro" className="mb-3">
          {mensagemDoConsole(transferir.error)}
        </Aviso>
      ) : null}
      <Botao type="submit" disabled={!destino || transferir.isPending} data-testid="chat-transferir">
        Transferir
      </Botao>
    </form>
  );
}

function Encerramento({ conversa, aoConcluir }: PropsDoPainel) {
  const encerrar = useEncerrarConversa(conversa.id);
  const chamados = useChamadosDaOrganizacao(conversa.organizacao_id);
  const [form, setForm] = useState<FormularioDeEncerramento>({
    ...ENCERRAMENTO_EM_BRANCO,
    assunto: (conversa.previa ?? "").slice(0, 80),
  });
  const [problema, setProblema] = useState<string | null>(null);

  function enviar(evento: FormEvent) {
    evento.preventDefault();
    const erro = validarEncerramento(form);
    setProblema(erro);
    if (erro) return;
    encerrar.mutate(form, {
      onSuccess: (resultado) =>
        aoConcluir(`Conversa encerrada. Registrada no chamado #${resultado.ticket_numero}.`),
    });
  }

  return (
    <form className="mt-3" onSubmit={enviar} noValidate data-testid="chat-encerramento">
      <p className="chat-painel__texto">
        Ao encerrar, a conversa vira registro no chamado: a transcrição entra como respostas públicas.
      </p>
      <div className="campo">
        <label className="campo__rotulo" htmlFor="chat-encerrar-modo">
          O que fazer com a conversa
        </label>
        <select
          id="chat-encerrar-modo"
          className="campo__controle"
          value={form.modo}
          onChange={(evento) => setForm({ ...form, modo: evento.target.value as FormularioDeEncerramento["modo"] })}
        >
          <option value="novo_chamado">Abrir um chamado novo</option>
          <option value="anexar">Anexar a um chamado existente</option>
        </select>
      </div>
      {form.modo === "novo_chamado" ? (
        <>
          <Campo
            rotulo="Assunto do chamado"
            obrigatorio
            value={form.assunto}
            maxLength={200}
            onChange={(evento) => setForm({ ...form, assunto: evento.target.value })}
          />
          <div className="campo">
            <label className="campo__rotulo" htmlFor="chat-encerrar-urgencia">
              Urgência
            </label>
            <select
              id="chat-encerrar-urgencia"
              className="campo__controle"
              value={form.urgencia}
              onChange={(evento) =>
                setForm({ ...form, urgencia: evento.target.value as FormularioDeEncerramento["urgencia"] })
              }
            >
              <option value="baixa">Baixa</option>
              <option value="media">Média</option>
              <option value="alta">Alta</option>
              <option value="urgente">Urgente</option>
            </select>
          </div>
        </>
      ) : (
        <div className="campo">
          <label className="campo__rotulo" htmlFor="chat-encerrar-chamado">
            Chamado da empresa
          </label>
          <select
            id="chat-encerrar-chamado"
            className="campo__controle"
            value={form.ticketId}
            onChange={(evento) => setForm({ ...form, ticketId: evento.target.value })}
          >
            <option value="">Escolha…</option>
            {(chamados.data ?? [])
              .filter((chamado) => chamado.tipo === "publico" && !chamado.mesclado_em_id)
              .map((chamado) => (
                <option key={chamado.id} value={chamado.id}>
                  #{chamado.numero} — {chamado.assunto}
                </option>
              ))}
          </select>
        </div>
      )}
      {problema ? (
        <Aviso tom="erro" className="mb-3">
          {problema}
        </Aviso>
      ) : null}
      {encerrar.isError ? (
        <Aviso tom="erro" className="mb-3">
          {mensagemDoConsole(encerrar.error)}
        </Aviso>
      ) : null}
      <Botao type="submit" variante="verde" disabled={encerrar.isPending} data-testid="chat-encerrar">
        Encerrar conversa
      </Botao>
    </form>
  );
}
